import { useSnackbar } from 'notistack';
import { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { storage } from '../../../utils/storage';
// @mui
import { alpha } from '@mui/material/styles';
import {
  Box,
  Divider,
  Typography,
  Stack,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Backdrop,
  CircularProgress
} from '@mui/material';
// routes
import { PATH_DASHBOARD, PATH_AUTH } from '../../../routes/paths';
// hooks
import useAuth, { UserRole } from '../../../hooks/useAuth';
import useIsMountedRef from '../../../hooks/useIsMountedRef';
import { useSettingsDrawer } from 'src/components/settings/drawer';
// components
import MyAvatar from '../../../components/MyAvatar';
import MenuPopover from '../../../components/MenuPopover';
import { IconButtonAnimate } from '../../../components/animate';

// ----------------------------------------------------------------------

const MENU_OPTIONS = [
  {
    label: 'Dashboard',
    linkTo: '/',
  },
  {
    label: 'Profile',
    linkTo: PATH_DASHBOARD.users.account,
  },
];

// ----------------------------------------------------------------------

export default function AccountPopover() {
  const navigate = useNavigate();

  const { user, logout, getUserRole } = useAuth();
  const currentUserRole = getUserRole();

  const isMountedRef = useIsMountedRef();

  const { enqueueSnackbar } = useSnackbar();

  const { onOpen: openSettingsDrawer } = useSettingsDrawer();

  const [open, setOpen] = useState<HTMLElement | null>(null);
  const [clearCacheDialogOpen, setClearCacheDialogOpen] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setOpen(event.currentTarget);
  };

  const handleClose = () => {
    setOpen(null);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate(PATH_AUTH.login, { replace: true });

      if (isMountedRef.current) {
        handleClose();
      }
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Unable to logout!', { variant: 'error' });
    }
  };

  const handleClearCache = () => {
    setClearCacheDialogOpen(true);
    handleClose();
  };

  const handleConfirmClearCache = async () => {
    try {
      setIsUpdating(true);
      setClearCacheDialogOpen(false);

      // Add a small delay to show the loading state
      await new Promise(resolve => setTimeout(resolve, 500));

      // Clear IndexedDB storage
      await storage.clear();

      // Clear localStorage
      localStorage.clear();

      // Clear sessionStorage
      sessionStorage.clear();

      // Clear cache API (if supported)
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(
          cacheNames.map(cacheName => caches.delete(cacheName))
        );
      }

      enqueueSnackbar('Cache cleared successfully!', { variant: 'success' });

      // Add another delay before reload
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Reload the page to reflect changes
      window.location.reload();

    } catch (error) {
      console.error('Error clearing cache:', error);
      enqueueSnackbar('Error clearing cache!', { variant: 'error' });
      setIsUpdating(false);
    }
  };

  const handleCancelClearCache = () => {
    setClearCacheDialogOpen(false);
  };

  return (
    <>
      <IconButtonAnimate
        onClick={handleOpen}
        sx={{
          p: 0,
          ...(open && {
            '&:before': {
              zIndex: 1,
              content: "''",
              width: '100%',
              height: '100%',
              borderRadius: '50%',
              position: 'absolute',
              bgcolor: (theme) => alpha(theme.palette.grey[900], 0.8),
            },
          }),
        }}
      >
        <MyAvatar />
      </IconButtonAnimate>

      <MenuPopover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleClose}
        sx={{
          p: 0,
          mt: 1.5,
          ml: 0.75,
          '& .MuiMenuItem-root': {
            typography: 'body2',
            borderRadius: 0.75,
          },
        }}
      >
        <Box sx={{ my: 1.5, px: 2.5 }}>
          <Typography variant="subtitle2" noWrap>
            {user?.displayName}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }} noWrap>
            {user?.email}
          </Typography>
        </Box>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <Stack sx={{ p: 1 }}>
          {currentUserRole != UserRole.STAFF ?

            MENU_OPTIONS.map((option) => (
              <MenuItem
                key={option.label}
                to={option.linkTo}
                component={RouterLink}
                onClick={handleClose}
              >
                {option.label}
              </MenuItem>
            ))

            : <></>}
          {currentUserRole == UserRole.ADMIN || currentUserRole == UserRole.DISTRIBUTOR || currentUserRole == UserRole.RETAILER ?
            <MenuItem to={PATH_DASHBOARD.companies.profile} component={RouterLink} onClick={handleClose}>
              Company Profile
            </MenuItem>
            :
            <></>
          }
          <MenuItem onClick={() => { openSettingsDrawer(); handleClose(); }}>
            Theme Settings
          </MenuItem>
          <MenuItem onClick={handleClearCache}>
            Update App
          </MenuItem>
        </Stack>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <MenuItem onClick={handleLogout} sx={{ m: 1 }}>
          Logout
        </MenuItem>
      </MenuPopover>

      {/* Clear Cache Confirmation Dialog */}
      <Dialog
        open={clearCacheDialogOpen}
        onClose={handleCancelClearCache}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reload to Apply Update</DialogTitle>
        <DialogContent>
          <Typography>
            A new version of the app is available. Reload now to apply the update and ensure the latest features and fixes are active.
          </Typography>
          <Typography component="ul" sx={{ mt: 2, pl: 2 }}>
            <li>Offline content will be refreshed</li>
            <li>App may take a few seconds to reload</li>
          </Typography>
          <Typography sx={{ mt: 2, color: 'warning.main' }}>
            This will reset stored data. Make sure to save your work before continuing.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelClearCache} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleConfirmClearCache} color="primary" variant="contained">
            Reload Now
          </Button>
        </DialogActions>
      </Dialog>

      {/* Full Screen Loading Overlay */}
      <Backdrop
        sx={{
          color: '#fff',
          zIndex: 9999,
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          flexDirection: 'column',
          gap: 2
        }}
        open={isUpdating}
      >
        <CircularProgress color="inherit" size={60} />
        <Typography variant="h6" color="inherit">
          Updating App...
        </Typography>
        <Typography variant="body2" color="inherit" sx={{ opacity: 0.8 }}>
          Please wait while we apply the latest updates
        </Typography>
      </Backdrop>
    </>
  );
}